import { setError } from "../store/slices/ErrorSlice";
import { clientAPI } from "./ClientService";

// List Management types backed by their own tables, read elsewhere through their own queries:
// edits must refresh those caches (client Housing / Company Branch dropdowns, Providers page).
const listManagementTags = (type) =>
  type === 'ClientHousing' ? ['ClientHousings'] : type === 'CompanyBranch' ? ['CompanyBranches'] : [];

const utilityApi = clientAPI.injectEndpoints({
  endpoints: (build) => ({
    getClientStatuses: build.query({
      query: () => ({
        url: `/api/utility/GetClientStatuses`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientHousings: build.query({
      query: () => ({
        url: `/api/Utility/GetClientHousings`,
      }),
      providesTags: ['ClientHousings'],
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientCounselors: build.query({
      query: () => ({
        url: `/api/Utility/GetClientCounselors`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getCompanyBranches: build.query({
      query: () => ({
        url: `/api/utility/GetCompanyBranches`,
      }),
      providesTags: ['CompanyBranches'],
      transformErrorResponse: (response) => {
        build.dispatch(setError(response.data.Message));
      },
    }),
    getTracks: build.query({
      query: () => ({
        url: `/api/utility/GetTracks`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getRelationshipToInsured: build.query({
      query: () => ({
        url: `/api/utility/ClientInsurance/dropdown/GetRelationshipToInsured`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getGender: build.query({
      query: () => ({
        url: `/api/utility/ClientInsurance/dropdown/Gender`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getRelationToPatient: build.query({
      query: () => ({
        url: `/api/utility/ClientInsurance/dropdown/RelationToPatient`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getPrimaryPayer: build.query({
      query: () => ({
        url: `/api/utility/ClientInsurance/dropdown/PrimaryPayer`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getGenders: build.query({
      query: () => ({
        url: `/api/utility/GetGenders`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getSexualOrientations: build.query({
      query: () => ({
        url: `/api/utility/GetSexualOrientations`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getGenderIdentities: build.query({
      query: () => ({
        url: `/api/utility/GetGenderIdentities`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getEthnicities: build.query({
      query: () => ({
        url: `/api/utility/GetEthnicities`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getMaritalStatuses: build.query({
      query: () => ({
        url: `/api/utility/GetMaritalStatuses`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getRequestedServices: build.query({
      query: () => ({
        url: `/api/utility/GetRequestedServices`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getClientServices: build.query({
      query: () => ({
        url: `/api/Utility/ClientService/dropdown/Service`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getLevelOfCare: build.query({
      query: () => ({
        url: `/api/Utility/ClientBasicInformation/dropdown/LevelOfCare`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    getDropDownOptions: build.query({
      query: (type) => ({
        url: `/api/Utility/dropdown/${type}`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data.Message };
      },
    }),
    // Attendance status/reason options (dbo.AttendanceStatusReasons).
    getAttendanceStatusReasons: build.query({
      query: () => ({
        url: `/api/Utility/dropdown/AttendanceStatus`,
      }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title };
      },
    }),

    // System Modules used for Role Permissions (Module type)
    getSystemModules: build.query({
      query: () => ({
        url: `/api/Utility/GetSystemModules`,
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.Message || response.data?.title };
      },
    }),

    getServicesDetail: build.query({
      query: () => {
        console.debug("[UtilityService/getServices] Building request");
        return {
          url: `/api/Utility/GetServices`,
        };
      },
      transformResponse: (response) => {
        console.debug("[UtilityService/getServices] transformResponse", response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error("[UtilityService/getServices] transformErrorResponse", response);
        return { message: response?.data?.Message || "Unknown error" };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        console.debug("[UtilityService/getServices] Query started", { arg });
        try {
          const { data } = await queryFulfilled;
          console.debug("[UtilityService/getServices] Query success", data);
        } catch (err) {
          console.error("[UtilityService/getServices] Query failed", err);
        }
      },
    }),
    // Before saving: which unbilled services the service's rates would re-rate (by effective date).
    // Nothing is saved. Returns { changedCount, changes: [{ serviceName, oldRate, newRate, serviceCount, firstDate, lastDate }] }.
    previewServiceRateImpact: build.mutation({
      query: (service) => ({
        url: `/api/Utility/UpdateService/RateImpact`,
        method: "POST",
        body: service,
      }),
    }),
    updateService: build.mutation({
      query: (service) => {
        console.log("[UtilityService/updateService] payload", service);
        return {
          url: `/api/Utility/UpdateService`,
          method: "POST",
          body: service,
        };
      },
      transformErrorResponse: (response) => {
        return { message: response.data.Message || response.data?.title };
      },
    }),
    getProviders: build.query({
      query: () => ({ url: `/api/Provider` }),
      providesTags: ['Providers'],
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),
    getProvider: build.query({
      query: (id) => ({ url: `/api/Provider/${id}` }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),
    upsertProvider: build.mutation({
      query: (provider) => ({
        url: `/api/Provider/Upsert`,
        method: 'POST',
        body: provider,
      }),
      invalidatesTags: ['Providers'],
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),
    getClientFileAttachmentTypes: build.query({
      query: () => ({ url: `/api/Utility/ClientFileAttachment/dropdown/GetClientFileAttachmentTypes` }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),

    // ── List Management (dbo.SelectOptions) ──
    getSelectOptionTypes: build.query({
      query: () => ({ url: `/api/Utility/ListManagement/Types` }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),
    getSelectOptionsByType: build.query({
      query: (type) => ({ url: `/api/Utility/ListManagement/${encodeURIComponent(type)}` }),
      transformErrorResponse: (response) => {
        return { message: response?.data?.Message || response?.data?.title || 'Unknown error' };
      },
    }),
    addSelectOption: build.mutation({
      query: (option) => ({
        url: `/api/Utility/ListManagement`,
        method: 'POST',
        body: { type: option.type, name: option.name, code: option.code ?? null },
      }),
      // Housing is edited here but read by the client Housing dropdown / Providers page.
      invalidatesTags: (result, error, option) => listManagementTags(option?.type),
      transformErrorResponse: (response) => {
        return { message: response?.data?.message || response?.data?.Message || response?.data?.title || 'Failed to add option' };
      },
    }),
    updateSelectOption: build.mutation({
      query: (option) => ({
        url: `/api/Utility/ListManagement/${option.id}`,
        method: 'PUT',
        body: { id: option.id, name: option.name, code: option.code ?? null, type: option.type },
      }),
      invalidatesTags: (result, error, option) => listManagementTags(option?.type),
      transformErrorResponse: (response) => {
        return { message: response?.data?.message || response?.data?.Message || response?.data?.title || 'Failed to update option' };
      },
    }),
    // { id, type }: type routes lists with their own table (ClientHousing), whose ids overlap.
    deleteSelectOption: build.mutation({
      query: ({ id, type }) => ({
        url: `/api/Utility/ListManagement/${id}${type ? `?type=${encodeURIComponent(type)}` : ''}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => listManagementTags(arg?.type),
      // 409 (in use) surfaces its message here.
      transformErrorResponse: (response) => {
        return { status: response?.status, message: response?.data?.message || response?.data?.Message || 'Failed to delete option' };
      },
    }),
  }),
  overrideExisting: false,
});
export const {
  useGetClientStatusesQuery,
  useGetClientCounselorsQuery,
  useGetClientHousingsQuery,
  useGetCompanyBranchesQuery,
  useGetTracksQuery,
  useGetRelationToPatientQuery,
  useGetRelationshipToInsuredQuery,
  useGetGenderQuery,
  useGetPrimaryPayerQuery,
  useGetGendersQuery,
  useGetSexualOrientationsQuery,
  useGetGenderIdentitiesQuery,
  useGetEthnicitiesQuery,
  useGetMaritalStatusesQuery,
  useGetRequestedServicesQuery,
  useGetClientServicesQuery,
  useGetLevelOfCareQuery,
  useGetHousesQuery,
  useGetDropDownOptionsQuery,
  useGetAttendanceStatusReasonsQuery,
  useGetSystemModulesQuery,
  useGetServicesDetailQuery,
  useUpdateServiceMutation,
  usePreviewServiceRateImpactMutation,
  useGetProvidersQuery,
  useGetProviderQuery,
  useUpsertProviderMutation,
  useGetClientFileAttachmentTypesQuery,
  useGetSelectOptionTypesQuery,
  useGetSelectOptionsByTypeQuery,
  useAddSelectOptionMutation,
  useUpdateSelectOptionMutation,
  useDeleteSelectOptionMutation,
} = utilityApi;
